<template>
  <div class="bizhub-assistant-panel" :class="{ dark: isDark }">
    <div class="bizhub-assistant-header">
      <v-icon size="small" class="mr-2">mdi-lifebuoy</v-icon>
      <span class="text-subtitle-2 font-weight-medium">業務管理アシスタント</span>
      <v-chip size="x-small" variant="tonal" class="ml-2">{{ screenLabel }}</v-chip>
      <v-spacer />
      <v-btn
        icon="mdi-plus-circle-outline"
        size="small"
        variant="text"
        title="新しい会話"
        :disabled="store.streaming"
        @click="startNew"
      />
      <v-menu v-model="historyMenu" location="bottom end" :z-index="2100" @update:model-value="onHistoryMenu">
        <template #activator="{ props: menuProps }">
          <v-btn v-bind="menuProps" icon="mdi-history" size="small" variant="text" title="過去の会話" :disabled="store.streaming" />
        </template>
        <v-list density="compact" max-height="420" min-width="280" class="py-0">
          <v-list-item v-if="historyLoading" title="読み込み中…" />
          <v-list-item v-else-if="!conversations.length" title="過去の会話はありません" />
          <v-list-item
            v-for="c in conversations"
            :key="c.id"
            :active="c.id === store.conversationId"
            @click="openConversation(c.id)"
          >
            <v-list-item-title class="text-body-2">{{ c.title || "（無題）" }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              {{ c.staff_name || "" }} · {{ formatDate(c.updated_at) }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn icon="mdi-close" size="small" variant="text" @click="$emit('close')" />
    </div>

    <div class="bizhub-assistant-hint text-caption">
      使い方の質問、在庫の確認、要望や気づきの記録ができます。答えられないことは記録して、後で小林さんと Claude が対応します。
    </div>

    <div ref="listEl" class="bizhub-assistant-messages">
      <div v-if="messages.length === 0" class="bizhub-assistant-empty text-caption">
        例:「袋詰めはどこから入力する？」<br />
        「缶バッチの在庫はいくつ？」<br />
        「伝票の一覧で品目で絞りたい（要望）」
      </div>

      <div v-for="(m, i) in messages" :key="i" class="bizhub-assistant-bubble" :class="m.role">
        <div class="bizhub-assistant-bubble-body" v-text="m.content" />
        <v-progress-circular
          v-if="m.role === 'assistant' && m.streaming && !m.content"
          indeterminate
          size="14"
          width="2"
          class="ml-1"
        />
        <div v-if="m.opened" class="bizhub-assistant-recorded">
          <v-chip size="x-small" color="primary" variant="tonal" prepend-icon="mdi-open-in-app">
            {{ m.opened }} を開きました
          </v-chip>
        </div>
        <div v-if="m.recorded" class="bizhub-assistant-recorded">
          <v-chip size="x-small" color="success" variant="tonal" prepend-icon="mdi-check">
            記録しました（小林さんと Claude が後で対応します）
          </v-chip>
        </div>
        <div v-if="m.role === 'assistant' && !m.streaming && m.id" class="bizhub-assistant-feedback">
          <v-btn
            size="x-small"
            variant="text"
            density="compact"
            :color="m.rating === 'up' ? 'success' : undefined"
            :class="{ 'rating-active': m.rating === 'up' }"
            :icon="m.rating === 'up' ? 'mdi-thumb-up' : 'mdi-thumb-up-outline'"
            aria-label="役に立った"
            title="役に立った"
            @click="rate(m, 'up')"
          />
          <v-btn
            size="x-small"
            variant="text"
            density="compact"
            :color="m.rating === 'down' ? 'error' : undefined"
            :class="{ 'rating-active': m.rating === 'down' }"
            :icon="m.rating === 'down' ? 'mdi-thumb-down' : 'mdi-thumb-down-outline'"
            aria-label="役に立たなかった"
            title="役に立たなかった"
            @click="rate(m, 'down')"
          />
        </div>
      </div>

      <div v-if="toolActivity" class="bizhub-assistant-tool-activity text-caption">
        <v-progress-circular indeterminate size="12" width="2" class="mr-1" />
        {{ toolActivity }}
      </div>
    </div>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="ma-2"
      closable
      @click:close="error = ''"
    >
      {{ error }}
    </v-alert>

    <div class="bizhub-assistant-input">
      <v-textarea
        v-model="draft"
        placeholder="メッセージを入力（Enter で送信 / Shift+Enter で改行）"
        rows="1"
        max-rows="6"
        auto-grow
        variant="outlined"
        density="compact"
        hide-details
        class="chat-size-input"
        @keydown.enter.exact.prevent="send"
        @keydown.up="onArrowUp"
        @keydown.down="onArrowDown"
      />
      <v-btn
        icon="mdi-send"
        color="primary"
        size="small"
        class="ml-2"
        :loading="store.streaming"
        :disabled="!draft.trim() || store.streaming"
        @click="send"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useTheme } from "vuetify";
import { sendBizhubAssistantMessage } from "@/api/bizhubAssistantApi";
import { errorMessage, listConversations, rateAssistantMessage } from "@/api/bizhubApi";
import { useBizhubAssistantStore } from "@/stores/bizhubAssistantStore";
import { useBizhubStore } from "@/stores/bizhubStore";

const props = defineProps({
  // { screen: route.name, record_type: "item"|null, record_id: string|null }
  context: { type: Object, required: true },
});
defineEmits(["close"]);

const theme = useTheme();
const isDark = computed(() => theme.global.current.value.dark);

const store = useBizhubAssistantStore();
const bizhubStore = useBizhubStore();
const messages = computed(() => store.messages);

const draft = ref("");
const error = ref("");
const toolActivity = ref("");
const listEl = ref(null);

const historyMenu = ref(false);
const historyLoading = ref(false);
const conversations = ref([]);

// 上下矢印での入力履歴呼び出し（AiNpc パネルと同じ方式）。null = 非ナビゲーション中。
const historyIndex = ref(null);
function sentHistory() {
  return store.messages.filter((m) => m.role === "user").map((m) => m.content);
}
function onArrowUp(e) {
  const hist = sentHistory();
  if (historyIndex.value !== null && draft.value !== hist[historyIndex.value]) {
    historyIndex.value = null;
  }
  if (historyIndex.value === null) {
    if (draft.value !== "") return;
    if (!hist.length) return;
    historyIndex.value = hist.length - 1;
  } else if (historyIndex.value > 0) {
    historyIndex.value -= 1;
  } else {
    e.preventDefault();
    return;
  }
  draft.value = hist[historyIndex.value];
  e.preventDefault();
}
function onArrowDown(e) {
  const hist = sentHistory();
  if (historyIndex.value === null) return;
  if (draft.value !== hist[historyIndex.value]) {
    historyIndex.value = null;
    return;
  }
  if (historyIndex.value < hist.length - 1) {
    historyIndex.value += 1;
    draft.value = hist[historyIndex.value];
    e.preventDefault();
  } else {
    historyIndex.value = null;
    draft.value = "";
    e.preventDefault();
  }
}

const SCREEN_LABELS = {
  BizhubHome: "ダッシュボード",
  BizhubStock: "在庫一覧",
  BizhubMovements: "伝票",
  BizhubStocktake: "棚卸",
  BizhubDrafts: "AI 下書き",
  BizhubItems: "品目マスタ",
  BizhubParties: "取引先",
  BizhubContractors: "外注先",
  BizhubJobs: "案件一覧",
  BizhubInquiries: "問い合わせ",
  BizhubJobDetail: "案件の詳細",
  BizhubSchedule: "スケジュール",
  BizhubCustomers: "顧客",
  BizhubSuppliers: "仕入先",
  BizhubPartyDetail: "取引先の詳細",
  BizhubStaff: "担当者",
  BizhubFeedback: "フィードバック",
  BizhubItemDetail: "在庫詳細",
  BizhubMailSettings: "メール連携",
  BizhubEquipmentSets: "号機一覧",
  BizhubEquipmentSetDetail: "号機の詳細",
  BizhubEquipmentCalendar: "機材カレンダー",
  BizhubEquipmentItems: "個体一覧",
  BizhubEquipmentInventory: "機材の棚卸し",
};
const screenLabel = computed(() => SCREEN_LABELS[props.context?.screen] || "業務管理");

const TOOL_LABELS = {
  record_feedback: "記録しています…",
  lookup_stock: "在庫を調べています…",
  lookup_jobs: "案件を調べています…",
  list_tasks: "タスクを調べています…",
  open_screen: "画面を開いています…",
  find_free_sets: "空いている号機を調べています…",
  locate_sets: "号機の居場所を調べています…",
};
const router = useRouter();

const VISIBLE_MAX = 1500;
const uniq = (arr) => [...new Set(arr.map((t) => (t || "").replace(/\s+/g, " ").trim()).filter((t) => t && t.length <= 40))];
/**
 * いま画面に見えている見出し・欄のラベル・ボタン名・開いているダイアログを文字列にする。
 * アシスタントは画面の中身が見えないので、「表示されている物について聞かれる」ための文脈（Feedback #4/#7）。
 * DOM の文字だけ拾い、入力値は送らない。
 */
function captureVisible() {
  try {
    const main = document.querySelector("main.v-main");
    if (!main) return "";
    const notDock = (el) => !el.closest(".bizhub-assistant-dock");
    const texts = (root, sel) => uniq([...root.querySelectorAll(sel)].filter(notDock).map((e) => e.innerText || e.textContent));
    const lines = [];
    const heads = texts(main, "h1, h2, h3, .v-card-title");
    if (heads.length) lines.push(`見出し: ${heads.join(", ")}`);
    const fields = texts(main, ".v-field label, .v-label");
    if (fields.length) lines.push(`欄: ${fields.join(", ")}`);
    const buttons = texts(main, ".v-btn").filter((t) => !/^[\u{1F300}-\u{1FAFF}]$/u.test(t));
    if (buttons.length) lines.push(`ボタン: ${buttons.join(", ")}`);
    const ths = texts(main, "th");
    if (ths.length) lines.push(`表の列: ${ths.join(", ")}`);
    const dialog = [...document.querySelectorAll(".v-overlay--active .v-dialog .v-card")].filter(notDock)[0];
    if (dialog) {
      const dHeads = texts(dialog, ".v-card-title");
      const dFields = texts(dialog, ".v-field label, .v-label");
      const dButtons = texts(dialog, ".v-btn");
      lines.push(`開いているダイアログ: ${dHeads.join(", ") || "(無題)"}; 欄: ${dFields.join(", ")}; ボタン: ${dButtons.join(", ")}`);
    }
    return lines.join("\n").slice(0, VISIBLE_MAX);
  } catch (_) {
    return "";
  }
}

function formatDate(s) {
  return (s || "").slice(0, 16).replace("T", " ");
}

function startNew() {
  if (store.streaming) return;
  store.newConversation();
  error.value = "";
  toolActivity.value = "";
}

async function onHistoryMenu(open) {
  if (!open) return;
  historyLoading.value = true;
  try {
    conversations.value = (await listConversations()) || [];
  } catch (e) {
    error.value = errorMessage(e);
  } finally {
    historyLoading.value = false;
  }
}

async function openConversation(id) {
  historyMenu.value = false;
  error.value = "";
  try {
    await store.loadConversation(id);
    scrollToBottom();
  } catch (e) {
    error.value = errorMessage(e);
  }
}

async function rate(m, rating) {
  if (!m.id) return;
  if (!bizhubStore.requireStaff()) return;
  try {
    await rateAssistantMessage(m.id, rating);
    m.rating = rating;
  } catch (e) {
    error.value = errorMessage(e);
  }
}

async function scrollToBottom() {
  await nextTick();
  if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight;
}

// 一覧画面の「会話」リンク等で外から会話が読み込まれたら末尾へ
watch(() => store.conversationId, () => scrollToBottom());

async function send() {
  const text = draft.value.trim();
  if (!text || store.streaming) return;
  if (!bizhubStore.requireStaff()) return;
  error.value = "";

  store.messages.push({ role: "user", content: text });
  draft.value = "";
  historyIndex.value = null;

  store.messages.push({ role: "assistant", content: "", streaming: true, rating: "", recorded: false, opened: "" });
  // push 後の要素を取り直す（reactive proxy 経由で更新して描画に反映させる）
  const assistantMsg = store.messages[store.messages.length - 1];
  store.streaming = true;
  await scrollToBottom();

  // 履歴はサーバが持つので、会話 id と新しい発言だけを送る
  try {
    await sendBizhubAssistantMessage(
      { conversationId: store.conversationId, message: text, context: { ...props.context, visible: captureVisible() } },
      {
        onConversation: (id) => {
          store.conversationId = id;
        },
        onToken: (t) => {
          toolActivity.value = "";
          assistantMsg.content += t;
          scrollToBottom();
        },
        onToolCall: (name) => {
          toolActivity.value = TOOL_LABELS[name] || `${name} を実行中…`;
          scrollToBottom();
        },
        onToolResult: (d) => {
          toolActivity.value = "";
          if (d.name === "record_feedback" && d.ok) assistantMsg.recorded = true;
        },
        onOpenScreen: (d) => {
          // backend の open_screen ツール。ルート名＋params をそのまま router に渡す（読むだけ、書き込み無し）
          assistantMsg.opened = d.label || "画面";
          router.push(d.route).catch(() => {});
        },
        onDone: (d) => {
          assistantMsg.id = d.message_id;
          assistantMsg.streaming = false;
          store.streaming = false;
          toolActivity.value = "";
          scrollToBottom();
        },
        onError: (e) => {
          assistantMsg.streaming = false;
          store.streaming = false;
          toolActivity.value = "";
          if (!assistantMsg.content && !assistantMsg.recorded) {
            const idx = store.messages.indexOf(assistantMsg);
            if (idx !== -1) store.messages.splice(idx, 1);
          }
          error.value = e || "エラーが発生しました";
        },
      },
    );
  } catch (e) {
    error.value = errorMessage(e) || "エラーが発生しました";
  } finally {
    // done/error 無しで切れた・例外で抜けた場合も、パネルが送信中のまま固まらないようにする
    if (assistantMsg.streaming) assistantMsg.streaming = false;
    store.streaming = false;
    toolActivity.value = "";
  }
}
</script>

<style scoped>
.bizhub-assistant-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fafafa;
}
.bizhub-assistant-panel.dark {
  background: #1e1e1e;
}
.bizhub-assistant-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.bizhub-assistant-panel.dark .bizhub-assistant-header {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}
.bizhub-assistant-hint {
  padding: 6px 12px;
  color: #888;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.bizhub-assistant-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bizhub-assistant-empty {
  color: #999;
  line-height: 1.8;
  margin: auto 0;
}
.bizhub-assistant-recorded {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
  white-space: normal;
}
.bizhub-assistant-tool-activity {
  align-self: flex-start;
  color: #888;
  padding: 2px 4px;
}
.bizhub-assistant-bubble {
  max-width: 92%;
  padding: 8px 12px;
  border-radius: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.5;
}
.bizhub-assistant-bubble.user {
  align-self: flex-end;
  background: #1976d2;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.bizhub-assistant-bubble.assistant {
  align-self: flex-start;
  background: #fff;
  color: #222;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom-left-radius: 4px;
}
.bizhub-assistant-panel.dark .bizhub-assistant-bubble.assistant {
  background: #2a2a2a;
  color: #eee;
  border-color: rgba(255, 255, 255, 0.1);
}
.bizhub-assistant-feedback {
  margin-top: 2px;
  opacity: 0.7;
  white-space: normal;
}
.bizhub-assistant-feedback .rating-active {
  opacity: 1;
}
.bizhub-assistant-input {
  display: flex;
  align-items: flex-end;
  padding: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.bizhub-assistant-panel.dark .bizhub-assistant-input {
  border-top-color: rgba(255, 255, 255, 0.1);
}
/* 入力欄のフォントを小さめ（0.75rem = 12px）に。
   Vuetify 既定（.v-field__input 16px）が特異度で勝つため !important で確実に上書き。 */
.chat-size-input :deep(textarea),
.chat-size-input :deep(.v-field__input),
.chat-size-input :deep(input) {
  font-size: 0.75rem !important;
  line-height: 1.5 !important;
}
.chat-size-input :deep(textarea::placeholder) {
  font-size: 0.75rem !important;
}
</style>
